import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { FormControl, Grid, MenuItem, Select } from "@mui/material";
import logo from "./../../assets/safe_logo.png";
import { useResponsive } from "../../hooks/useResponsive";
import RoundButton from "../../components/RoundButton";
import { useDispatch, useSelector } from "react-redux";
import { authenticateLogin } from "../../redux/login/action";
import loginAccountScheme from "../../validations/loginAccountScheme";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import background_image from "./../../assets/png/background_image.png";
import storePersist, { localConstant } from "../../services/localStorage";
import { useTranslation } from "react-i18next";
import { ReactComponent as Frame } from "../../assets/svg/Frame.svg";
import { isMobileDeviceCheck, sendMessageToReactNative } from "../../utils";
import { BrandLogo } from "../../components/BrandLogo";
import FullPageModal from "../../components/FullPageLoader";

const LoginPage = () => {
  const { isLoading } = useSelector((state) => state.loginReducer);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const smDown = useResponsive("down", "sm");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(loginAccountScheme),
  });

  useEffect(() => {
    if (
      storePersist.get(localConstant.API_ACCESS_TOKEN) &&
      storePersist.get(localConstant.USER_PROFILE)
    ) {
      const userType = storePersist.get(localConstant.USER_PROFILE);
      navigate(userType === "OWNER" ? "/ownerDashboard" : "/operatorDashboard");
    }
  }, []);

  useEffect(() => {
    sendMessageToReactNative(`GET_NATIVE_PLATFORM_NAME_EVENT`);
  }, []);

  const formLogin = (values) => {
    dispatch(
      authenticateLogin(values, (data) => {
        if (
          data?.entity_user_status === "ACTIVE" &&
          data?.entity_role_code === "OWNER"
        ) {
          if (
            storePersist.get(localConstant.AGREEMENT_STATUS) !== "COMPLTD" &&
            storePersist.get(localConstant.AGREEMENT_STATUS) !== "IN_PRGRS"
          ) {
            navigate("/ownerDashboard");
          } else {
            navigate("/ownerDashboard");
          }
        } else if (
          data?.entity_user_status === "ACTIVE" &&
          data?.entity_role_code === "DRIVER"
        ) {
          navigate("/operatorDashboard", { state: data });
        } else if (data?.entity_user_status === "INACTIVE") {
          const encodedParams = encodeURIComponent(
            `id=${data?.entity_id}&email=${data?.entity_email}`,
          );
          navigate(`/emailVerification?${encodedParams}`, {
            state: data,
          });
        } else {
        }
      }),
    );
  };

  const [showPassword, setShowPassword] = useState(false);
  const [appVersion, setAppVersion] = useState();
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const { i18n, t } = useTranslation();

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  const [language, setLanguage] = useState(i18n?.language?.split("-")?.[0]);

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
    changeLanguage(e.target.value);
    storePersist.set(localConstant.LANGUAGE, e.target.value);
  };

  const onMessageFromRN = (message) => {
    try {
      const { action, payload } = JSON.parse(message);
      if (action === "App_Version") {
        storePersist.set(localConstant.APP_VERSION, payload);
        setAppVersion(payload.toString());
      }
      if (action === "Is_native_app_launched") {
        storePersist.set(localConstant.IS_NATIVE_APP_LAUNCHED, payload);
      }
      if (action === "NATIVE_PLATFORM") {
        storePersist.set(localConstant.NATIVE_PLATFORM, payload);
      }
    } catch (error) {
      console.error("Error parsing message:", error);
    }
  };

  // Attach the handler to `window` so we can access it from
  // scripts injected by React Native WebView.

  // dummy commit

  window.onMessageFromRN = onMessageFromRN;
  return (
    <Grid
      style={{
        paddingTop: 20,
        position: "relative",
        height: "100vh",
        backgroundColor: "#80BCFF4F",
        backgroundImage: `url(${background_image})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid
        flex={3}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Grid
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={{ xs: "100%", md: "50%" }}
        >
          <img src={logo} alt="Your Alt Text" />
        </Grid>
      </Grid>
      <Grid
        flex={7}
        display={"flex"}
        justifyContent={"center"}
        alignItems={{ xs: "center", md: "flex-start" }}
      >
        <Grid
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={{ xs: "100%", md: "40%" }}
          backgroundColor="white"
          borderRadius={2}
          height={{ xs: "100%", md: "auto" }}
          position="relative"
          flexDirection={"column"}
          overflowY={{ xs: "scroll" }}
        >
          <Grid width={{ xs: "90%", md: "70%" }} pt={2}>
            <form
              onSubmit={handleSubmit((data) => formLogin(data))}
              width={"100%"}
            >
              <Stack spacing={2}>
                <FormControl
                  sx={{
                    m: 0,
                    minWidth: 120,
                    position: "absolute",
                    top: "1%",
                    right: "1%",
                  }}
                  size="small"
                >
                  <Select
                    labelId="language_dropdown_label"
                    id="language_dropdown"
                    value={language}
                    label=""
                    onChange={handleLanguageChange}
                    sx={{
                      color: "grey",
                      borderColor: "grey",
                      border: "none",
                      "& .MuiSvgIcon-root": { color: "grey" },
                      "&:before": { borderColor: "grey" },
                      "&:after": { borderColor: "grey" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "grey",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid lightgrey !important",
                      },
                    }}
                    renderValue={(selected) => (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {/* This is where you insert your icon or Frame component if it's not an icon */}
                        <Frame />
                        {/* Display the selected language text */}
                        {selected === "en"
                          ? "English"
                          : selected === "es"
                            ? "Español"
                            : "Русский"}
                      </Box>
                    )}
                  >
                    <MenuItem value={"en"}>English</MenuItem>
                    <MenuItem value={"es"}>Español</MenuItem>
                    <MenuItem value={"ru"}>Русский</MenuItem>
                  </Select>
                </FormControl>
                <Typography
                  variant="titleText"
                  color="primary.main"
                  fontSize={{ xs: "16px", md: "22px" }}
                >
                  {t("pages.login.pageHeading")}
                </Typography>
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("pages.login.text15")}
                      name="username"
                      type="email"
                      error={!!errors.username}
                      helperText={errors.username?.message}
                    />
                  )}
                ></Controller>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      minLength="8"
                      name="password"
                      label={t("pages.login.text02")}
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  )}
                ></Controller>
              </Stack>
              <RoundButton
                sx={{ mt: 5, mb: 3 }}
                size="large"
                fullWidth
                variant="contained"
                type="submit"
                color="primary"
                rounded
              >
                <Typography variant="buttonText" color="common.buttonText">
                  {t("pages.login.text06")}
                </Typography>
              </RoundButton>
            </form>
          </Grid>
          <Stack>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Typography
                variant="smallForgotText"
                color="common.versionText"
                alignItems="center"
                justifyContent="center"
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => {
                  navigate("/enter_username");
                }}
              >
                {t("pages.login.text07")}?
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Typography
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                color="primary.main"
                variant="smallLinkText"
                underline="hover"
                onClick={() => {
                  navigate("/signup");
                }}
              >
                {t("pages.login.text08")}
              </Typography>
            </Stack>
          </Stack>
          {isMobileDeviceCheck() && (
            <Grid
              spacing={3}
              display={"flex"}
              alignItems="flex-end"
              justifyContent="flex-end"
              width={"100%"}
            >
              <Typography
                variant="versionText"
                color="common.versionText"
                textAlign={"end"}
                mr={3}
              >
                {appVersion
                  ? appVersion
                  : storePersist.get(localConstant.APP_VERSION)}
              </Typography>
            </Grid>
          )}
          <BrandLogo />
        </Grid>
      </Grid>
      {isLoading && <FullPageModal />}
    </Grid>
  );
};

export default LoginPage;
